import * as _ from 'lodash-es';
import type { IframeHTMLAttributes, ReactNode } from 'react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import ignoreInChromatic from '../../utils/ignoreInChromatic';
import findClosestScrollingContainer from '../utils/findClosestScrollingContainer';

function usePreventScrollJump({
  iframeRef,
}: {
  iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
}) {
  useEffect(() => {
    if (!iframeRef.current) return undefined;
    const scrollingContainer = findClosestScrollingContainer(
      iframeRef.current.parentElement,
    );
    if (!scrollingContainer) return undefined;

    let prevTop = 0;
    let isIframeFocused = false;
    let didDetectInitialScroll = false;
    const handleScroll = () => {
      if (isIframeFocused && scrollingContainer.scrollTop === 0) {
        scrollingContainer.scrollTop = prevTop;
      }

      const hasScrolledUp =
        scrollingContainer.scrollTop < prevTop &&
        scrollingContainer.scrollTop < SCROLL_OFFSET;

      if (!didDetectInitialScroll && hasScrolledUp) {
        scrollingContainer.scrollTop = prevTop;
        didDetectInitialScroll = true;
      }

      prevTop = scrollingContainer.scrollTop;
    };

    const handleBlur = () => {
      isIframeFocused =
        document.activeElement instanceof HTMLIFrameElement &&
        document.activeElement.name === iframeRef.current?.name;
      prevTop = scrollingContainer.scrollTop;
    };

    const handleFocus = () => {
      isIframeFocused = false;
    };

    window.addEventListener('blur', handleBlur, true);
    window.addEventListener('focus', handleFocus, true);
    scrollingContainer.addEventListener('scroll', handleScroll, false);

    return () => {
      window.removeEventListener('blur', handleBlur, true);
      window.removeEventListener('focus', handleFocus, true);
      scrollingContainer.removeEventListener('scroll', handleScroll, false);
    };
  }, [iframeRef]);
}

function usePreventFocusStealing({
  iframeRef,
  isSelected,
}: {
  iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
  isSelected?: boolean;
}) {
  const activeElementRef = useRef<EventTarget | null>(null);

  useLayoutEffect(() => {
    if (!iframeRef.current) return undefined;
    const scrollingContainer = findClosestScrollingContainer(
      iframeRef.current.parentElement,
    );
    if (!scrollingContainer) return undefined;

    activeElementRef.current = document.activeElement;

    const handleCurrentFrameFocus = (e: FocusEvent) => {
      activeElementRef.current = e.target;
    };

    // The blur listener is called also when the iframe is focused.
    // Need to return the focus back to the previously focused element.
    const handleBlur = () => {
      _.defer(() => {
        if (
          !isSelected &&
          document.activeElement instanceof HTMLIFrameElement &&
          document.activeElement.name === iframeRef.current?.name &&
          activeElementRef.current instanceof HTMLElement
        ) {
          // Can't focus the body, so blurring the iframe instead.
          if (activeElementRef.current.tagName === 'BODY') {
            iframeRef.current.blur();
          } else {
            activeElementRef.current.focus();
          }
        }
      });
    };

    window.addEventListener('focus', handleCurrentFrameFocus, true);
    window.addEventListener('blur', handleBlur, true);

    return () => {
      window.removeEventListener('focus', handleCurrentFrameFocus, true);
      window.removeEventListener('blur', handleBlur, true);
    };
  }, [iframeRef, isSelected]);
}

interface IframeWithFallbackProps
  extends IframeHTMLAttributes<HTMLIFrameElement> {
  type: string;
  fallback: ReactNode;
  selected?: boolean;
}

const EMBEDS_WITH_SIDE_EFFECTS = new Set([
  'google-sheet',
  'google-doc',
  'google-slide',
  'coda',
]);
const SCROLL_OFFSET = 20;

const IframeWithSideEffects = ({
  selected,
  ...props
}: React.IframeHTMLAttributes<HTMLIFrameElement> & { selected?: boolean }) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  usePreventScrollJump({ iframeRef });
  usePreventFocusStealing({ iframeRef, isSelected: selected });

  useEffect(() => {
    const iframeEl = iframeRef.current;
    const containerEl = containerRef.current;
    if (!iframeEl || !containerEl) return undefined;

    // We make use of the fact that elements with display: none will not
    // be considered when Element#scrollIntoView is called. Therefore,
    // when the iframe is not intersecting, we can prevent the iframe from
    // hijacking the scroll position.
    iframeEl.style.display = 'none';

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        iframeEl.style.display = entry.isIntersecting ? 'block' : 'none';
      });
    });

    observer.observe(containerEl);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div style={{ height: '100%' }} ref={containerRef}>
      {/* eslint-disable-next-line jsx-a11y/iframe-has-title */}
      <iframe ref={iframeRef} {...props} />
    </div>
  );
};

const IframeWithFallback = ({
  fallback,
  type,
  selected,
  ...props
}: IframeWithFallbackProps) => {
  const nameRef = useRef<string | null>(null);
  const readOnly = useAppSelector((state) => state.editor.readOnly);

  const getIframeName = () => {
    if (nameRef.current) {
      return nameRef.current;
    }
    const name = _.uniqueId('iframe_with_fallback');
    nameRef.current = name;
    return name;
  };

  const [isShowingFallback, setIsShowingFallback] = useState(false);

  useEffect(() => {
    const removeListener = window.desktop?.onIframeBlock?.(
      getIframeName(),
      () => {
        setIsShowingFallback(true);
      },
    );
    return removeListener;
  }, []);

  const sharedProps = {
    ...props,
    ...ignoreInChromatic(),
    name: getIframeName(),
  };

  if (isShowingFallback) return <>{fallback}</>;

  if (!readOnly && EMBEDS_WITH_SIDE_EFFECTS.has(type)) {
    return <IframeWithSideEffects selected={selected} {...sharedProps} />;
  }

  // eslint-disable-next-line jsx-a11y/iframe-has-title
  return <iframe {...sharedProps} />;
};

export default IframeWithFallback;
